import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFirestore } from '../contexts/FirestoreContext';
import { doc, getDoc, collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import PageHeader from '../components/PageHeader';
import { message } from 'antd';
import { 
  isCmtRole, 
  validateRole
} from '../models/role.js';
import DigitalProductUploader from '../components/DigitalProductUploader';
import DigitalProductVersionHistory from '../components/DigitalProductVersionHistory';
import signedUrlService from '../services/signedUrlService';
import { getDocumentStoragePath, constructCmtProductPath } from '../utils/storagePathExtractor';

import { HistoryOutlined, DownloadOutlined } from '@ant-design/icons';

export default function DigitalProductViewer() {
  const { productId, cmtId: urlCmtId } = useParams();
  const { db } = useFirestore();
  const { currentUser } = useAuth();
  const { activeCmt } = useMultiCmt();
  const [product, setProduct] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionHistoryVisible, setVersionHistoryVisible] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const [signedUrlLoading, setSignedUrlLoading] = useState(false);

  const isSuperAdmin = currentUser?.isSuperAdmin;

  // Get effective CMT ID from URL params or user context
  // New model only: require CMT id for non-super-admin
  const assignedFirst = Array.isArray(currentUser?.assignedCmtIds) && currentUser.assignedCmtIds.length > 0
    ? currentUser.assignedCmtIds[0]
    : null;
  const fromAssignments = Array.isArray(currentUser?.cmtAssignments)
    ? (currentUser.cmtAssignments.find(a => a?.isActive)?.cmtId || currentUser.cmtAssignments[0]?.cmtId)
    : null;
  const effectiveCmtId = urlCmtId || activeCmt?.id || fromAssignments || assignedFirst || null;

  // Check if user has edit permissions using role validation
  const canEditProducts = isSuperAdmin || (
    currentUser?.role && isCmtRole(validateRole(currentUser.role))
  );



  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        // If super admin, only get the master product
        if (isSuperAdmin) {
          const productRef = doc(db, 'digital_products', productId);
          const productDoc = await getDoc(productRef);
          
          if (!productDoc.exists()) {
            throw new Error('Product not found');
          }

          const productData = {
            id: productDoc.id,
            ...productDoc.data()
          };
          setProduct(productData);
          setLoading(false);
          return;
        }

        // For non-super admin users, require a CMT context and get product + versions
        const productRef = doc(db, 'digital_products', productId);
        const productDoc = await getDoc(productRef);
        
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }

        const productData = {
          id: productDoc.id,
          ...productDoc.data()
        };
        setProduct(productData);

        // Get versions from Firestore
        if (!effectiveCmtId) {
          throw new Error('Missing CMT ID');
        }
        const versionsRef = collection(db, 'c3_cmt_digital_products');

        const versionsQuery = query(
          versionsRef,
          where('originalProductId', '==', productId),
          where('cmtId', '==', effectiveCmtId)
        );

        const versionsSnapshot = await getDocs(versionsQuery);

        const versionsData = versionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter out versions that have the template URL (these are not real uploaded versions)
        const templateUrl = productData.downloadURL;
        const actualUploadedVersions = versionsData.filter(version => 
          version.downloadURL && version.downloadURL !== templateUrl
        );

        // Sort by version number in descending order (highest version first)
        const sortedVersions = actualUploadedVersions.sort((a, b) => {
          return (b.version || 0) - (a.version || 0);
        });

        // Set all versions (for version history), but prioritize actual uploaded ones
        setVersions(versionsData.sort((a, b) => (b.version || 0) - (a.version || 0)));

        // Set selected version: prioritize uploaded versions, fall back to template
        if (sortedVersions.length > 0) {
          setSelectedVersion(sortedVersions[0]);
        } else if (versionsData.length > 0) {
          // No uploaded versions, but template versions exist - show the latest template
          const latestTemplate = versionsData[0]; // Already sorted by version number
          setSelectedVersion(latestTemplate);
        } else {
          setSelectedVersion(null);
        }

      } catch (err) {
        console.error('🚨 DETAILED ERROR loading product data:', {
          error: err,
          message: err.message,
          code: err.code,
          productId: productId,
          effectiveCmtId: effectiveCmtId,
          isSuperAdmin: isSuperAdmin,
          currentUser: {
            uid: currentUser?.uid,
            role: currentUser?.role,
            orgId: currentUser?.orgId
          },
          activeCmt: activeCmt
        });
        setError(`Failed to load product data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (!productId) {
      setError('Missing product ID');
      setLoading(false);
      return;
    }

    if (!isSuperAdmin && !effectiveCmtId) {
      setError('Missing CMT ID');
      setLoading(false);
      return;
    }

    fetchProduct();
  }, [productId, effectiveCmtId, db, isSuperAdmin]);

  // Generate signed URL when selectedVersion changes
  useEffect(() => {
    if (selectedVersion?.downloadURL) {
      generateSignedUrlForVersion(selectedVersion);
    }
  }, [selectedVersion]);

  const generateSignedUrlForVersion = async (version) => {
    try {
      setSignedUrlLoading(true);
      console.log('🔐 Generating signed URL for PDF viewing:', version.fileName);
      
      // Get storage path using utility
      const fallbackPath = constructCmtProductPath(effectiveCmtId, productId, version.fileName);
      const storagePath = getDocumentStoragePath({ url: version.downloadURL }, fallbackPath);

      // Generate signed URL for viewing (15 minutes expiry)
      const viewingSignedUrl = await signedUrlService.generateSignedUrl(storagePath, 15);
      setSignedUrl(viewingSignedUrl);
      
      console.log('✅ Signed URL for PDF viewing generated successfully');
      
    } catch (error) {
      console.error('❌ Error generating signed URL for PDF viewing:', error);
      // Fallback: use original URL
      setSignedUrl(version.downloadURL);
    } finally {
      setSignedUrlLoading(false);
    }
  };

  const handleDownloadVersion = async () => {
    try {
      console.log('🔐 Initiating download with signed URL');
      
      const downloadUrl = signedUrl || selectedVersion?.downloadURL;
      
      if (!downloadUrl) {
        message.error('Download URL not available');
        return;
      }
      
      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = selectedVersion?.fileName || 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Download initiated successfully');
      
    } catch (error) {
      console.error('❌ Error downloading document:', error);
      message.error('Failed to download document. Please try again.');
    }
  };

  const handleUploadComplete = async (fileDetails) => {
    try {
      if (!effectiveCmtId) {
        message.error('CMT ID is required for upload');
        return;
      }

      if (!productId) {
        message.error('Product ID is required for upload');
        return;
      }

      const versionData = {
        originalProductId: productId,
        cmtId: effectiveCmtId,
        version: versions.length + 1,
        uploadedBy: currentUser.displayName || currentUser.email || 'Unknown',
        uploadedAt: serverTimestamp(),
        fileName: fileDetails.fileName,
        fileSize: fileDetails.fileSize,
        downloadURL: fileDetails.downloadURL,
        storagePath: fileDetails.storagePath,
        metadata: {
          contentType: fileDetails.contentType,
          lastModified: new Date().toISOString()
        }
      };

      // Add the version document to Firestore
      const docRef = await addDoc(collection(db, 'c3_cmt_digital_products'), versionData);
      
      // Create the new version object with the document ID
      const newVersion = {
        id: docRef.id,
        ...versionData,
        uploadedAt: new Date()
      };

      // Update the versions state
      setVersions(prevVersions => [newVersion, ...prevVersions]);
      setSelectedVersion(newVersion);
      message.success('Version uploaded successfully');
    } catch (err) {
      console.error('Error saving version:', err);
      message.error('Failed to save version');
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <PageHeader title="Digital Product Viewer" />
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <PageHeader title="Digital Product Viewer" />
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-4">
        <PageHeader title="Digital Product Viewer" />
        <div className="alert alert-warning" role="alert">
          Product not found
        </div>
      </div>
    );
  }

  return (
    <div className="p-0" style={{ height: 'calc(100vh - 64px)' }}>
      <PageHeader title={product.name} />

      <div className="row g-0" style={{ height: 'calc(100% - 64px)' }}>
        {/* PDF Preview */}
        <div className={`col-${isSuperAdmin ? '12' : '12'} h-100`}>
          <div className="card shadow-sm h-100">
            <div className="card-body p-0 h-100">
              {isSuperAdmin ? (
                product.downloadURL ? (
                  <div className="pdf-preview h-100" style={{ backgroundColor: 'white' }}>
                    <iframe
                      src={`${product.downloadURL}#toolbar=0&navpanes=0`}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        border: 'none',
                        backgroundColor: 'white',
                        display: 'block'
                      }}
                      title="PDF Preview"
                    />
                  </div>
                ) : (
                  <div className="alert alert-warning">
                    No PDF preview available for this product.
                  </div>
                )
              ) : versions.length === 0 && product.downloadURL ? (
                <>
                  {/* Action Buttons for Client View - Base Template */}
                  <div className="d-flex justify-content-end gap-2 p-2 border-bottom">
                      <a
                        href={product.downloadURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn d-flex align-items-center"
                        style={{ backgroundColor: '#39325b', color: 'white' }}
                      >
                        <DownloadOutlined className="me-2" />
                        Download Template
                      </a>
                    {canEditProducts && (
                      <DigitalProductUploader 
                        productId={productId}
                        cmtId={effectiveCmtId}
                        onUploadComplete={handleUploadComplete}
                      />
                    )}
                  </div>
                  {/* Show Base Template */}
                  <div className="pdf-preview h-100" style={{ backgroundColor: 'white' }}>
                    <iframe
                      src={`${product.downloadURL}#toolbar=0&navpanes=0`}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        border: 'none',
                        backgroundColor: 'white',
                        display: 'block'
                      }}
                      title="PDF Preview - Base Template"
                    />
                  </div>
                  {canEditProducts && (
                    <div className="alert alert-info position-absolute" style={{ 
                      bottom: '20px', 
                      left: '20px', 
                      right: '20px', 
                      zIndex: 10,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #b3d4fc'
                    }}>
                      <small>
                        <strong>Template View:</strong> This is the base template. You can download it, customize it offline, then upload your completed version using the upload button above.
                      </small>
                    </div>
                  )}
                </>
              ) : versions.length === 0 ? (
                <>
                  {/* No template available at all */}
                  <div className="d-flex justify-content-end gap-2 p-2 border-bottom">
                    {canEditProducts && (
                      <DigitalProductUploader 
                        productId={productId}
                        cmtId={effectiveCmtId}
                        onUploadComplete={handleUploadComplete}
                      />
                    )}
                  </div>
                  <div className="alert alert-warning">
                    <h5>No template available</h5>
                    <p className="mb-0">This digital product doesn't have a template available yet.</p>
                  </div>
                </>
              ) : selectedVersion ? (
                <>
                  {/* Action Buttons for Client View */}
                  <div className="d-flex justify-content-end gap-2 p-2 border-bottom">
                    {canEditProducts && (
                      <DigitalProductUploader 
                        productId={productId}
                        cmtId={effectiveCmtId}
                        onUploadComplete={handleUploadComplete}
                      />
                    )}
                    {selectedVersion && (
                      <button
                        onClick={handleDownloadVersion}
                        className="btn d-flex align-items-center"
                        style={{ backgroundColor: '#39325b', color: 'white' }}
                        disabled={signedUrlLoading}
                      >
                        <DownloadOutlined className="me-2" />
                        {signedUrlLoading ? 'Preparing...' : 'Download'}
                      </button>
                    )}
                    <button
                      className="btn d-flex align-items-center"
                      onClick={() => setVersionHistoryVisible(true)}
                      style={{ backgroundColor: '#39325b', color: 'white' }}
                    >
                      <HistoryOutlined className="me-2" />
                      Version History ({versions.length})
                    </button>
                  </div>
                  <div className="pdf-preview h-100" style={{ backgroundColor: 'white' }}>
                    {signedUrlLoading ? (
                      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
                        <div className="spinner-border text-primary mb-3" role="status">
                          <span className="visually-hidden">Preparing secure document view...</span>
                        </div>
                        <h5 className="mb-2">Preparing Secure View</h5>
                        <p className="text-muted">Generating time-limited access URL for enhanced security...</p>
                      </div>
                    ) : (
                      <iframe
                        src={`${signedUrl || selectedVersion.downloadURL}#toolbar=0&navpanes=0`}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          border: 'none',
                          backgroundColor: 'white',
                          display: 'block'
                        }}
                        title="PDF Preview"
                      />
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Action Buttons for Client View */}
                  <div className="d-flex justify-content-end gap-2 p-2 border-bottom">
                    {canEditProducts && (
                      <DigitalProductUploader 
                        productId={productId}
                        cmtId={effectiveCmtId}
                        onUploadComplete={handleUploadComplete}
                      />
                    )}
                    <button
                      className="btn d-flex align-items-center"
                      onClick={() => setVersionHistoryVisible(true)}
                      style={{ backgroundColor: '#39325b', color: 'white' }}
                    >
                      <HistoryOutlined className="me-2" />
                      Version History ({versions.length})
                    </button>
                  </div>
                  <div className="alert alert-info">
                    Please select a version to view.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Version History Drawer for Client View */}
        {!isSuperAdmin && (
          <DigitalProductVersionHistory
            versions={versions}
            selectedVersion={selectedVersion}
            onVersionSelect={setSelectedVersion}
            visible={versionHistoryVisible}
            onClose={() => setVersionHistoryVisible(false)}
          />
        )}
      </div>
    </div>
  );
} 